import React, { useCallback, useMemo } from 'react';
import {
  DateField,
  RecordContextProvider,
  useGetOne,
  usePermissions,
  useRecordContext,
  useStore,
} from 'react-admin';

import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { Loop } from '@mui/icons-material';

import { ICase } from '../../../../utilities/schemas/case';
import AiAnalysisForm from './AiAnalysisForm';
import AiAnalysisDisplay from './AiAnalysisDisplay';
import { ICaseAnalysis } from '../../../../utilities/schemas/caseAnalysis';
import hasAccess from '../../../../utilities/hasAccess';
import actions, { Actions } from '../../../../constants/actions';
import agentModes, { AgentMode } from '../../../../constants/agentModes';
import analysisTypes, { analysisTypeChoices } from '../../../../utilities/schemas/analysisTypes';
import breakpoints from '../../../../theme/breakpoints';
import aiGenerationStatuses from '../../../../constants/aiGenerationStatuses';
import isAnalysisExpired from '../../../../utilities/isAnalysisExpired';

const AiPanelContent = ({
  closeDialog,
}: {
  closeDialog: () => void;
}) => {
  const record = useRecordContext<ICase>();
  const [agentMode, setAgentMode] = useStore<AgentMode>('agentMode', agentModes.FETCHING);
  const { permissions } = usePermissions();

  const canGenerateNewAnalysis = hasAccess(permissions, actions.ANALYSIS_CREATE);

  const {
    data: analysisData,
    refetch,
  } = useGetOne<ICaseAnalysis>(
    `cases/${record?.id}/analysis`,
    { id: 'latest' },
    {
      refetchInterval: ({ state }) => {
        if (!state.data) return false;
        if (state.data.status !== aiGenerationStatuses.CREATED) return false;
        if (isAnalysisExpired(state.data)) return false;
        return 1000;
      },
      onSettled(data, error) {
        if (error && error.message === 'Analysis not found' && canGenerateNewAnalysis) {
          setAgentMode(agentModes.CREATE);
        } else if (data && isAnalysisExpired(data)) {
          setTimeout(() => {
            setAgentMode(agentModes.VIEW);
          }, 600);
        } else if (data?.status === aiGenerationStatuses.CREATED) setAgentMode(agentModes.LOADING);
        else {
          setTimeout(() => {
            setAgentMode(agentModes.VIEW);
          }, 600);
        }
      },
    },
  );

  const effectiveAnalysisData = useMemo(() => {
    if (!analysisData) return analysisData;
    if (isAnalysisExpired(analysisData)) {
      return { ...analysisData, status: aiGenerationStatuses.ERROR } as ICaseAnalysis;
    }
    return analysisData;
  }, [analysisData]);
  const onSuccessStartAnalysis = useCallback(() => {
    refetch();
  }, [refetch]);

  const analysisChoice = useMemo(
    () => analysisTypeChoices
      .find(({ id }) => id === analysisData?.inputParameters.analysisType),
    [analysisData?.inputParameters.analysisType],
  );

  if (!record) return null;

  return (
    <Box gap={6} padding={4} display="flex" flexDirection="column">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        data-testid="caseAgentPanelHeader"
      >
        <Box display="flex" alignItems="center">
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="row" gap={2} alignItems="center">
              <Typography variant="h4">Case agent</Typography>
            </Box>
            <Typography variant="label">AI-powered case analysis</Typography>
          </Box>
        </Box>
        <IconButton onClick={closeDialog}>
          <CancelIcon />
        </IconButton>
      </Box>
      {agentMode === agentModes.FETCHING && (
        <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
          <CircularProgress />
        </Box>
      )}
      {(
        agentMode === agentModes.EDIT
          || agentMode === agentModes.VIEW
          || agentMode === agentModes.LOADING
      ) && (
        <RecordContextProvider value={effectiveAnalysisData}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={4}>
            <Box display="flex" flexDirection="column" gap={2}>
              {analysisChoice && (
                <Box>
                  <Chip
                    label={analysisChoice?.name}
                    color="default"
                    variant="filled"
                  />
                </Box>
              )}
              {analysisData?.status !== aiGenerationStatuses.ERROR && (
                <Typography variant="smallTitle">
                  Generated:
                  {' '}
                  <DateField source="finishedAt" emptyText="-" showTime />
                </Typography>
              )}
            </Box>
            {agentMode !== agentModes.LOADING && canGenerateNewAnalysis && (
              <Button
                variant="outlined"
                onClick={() => setAgentMode(agentModes.CREATE)}
                size="medium"
                startIcon={<Loop />}
              >
                New analysis
              </Button>
            )}
          </Box>
          <AiAnalysisDisplay />
        </RecordContextProvider>
      )}
      {agentMode === agentModes.CREATE && (
        <AiAnalysisForm
          caseId={record.id}
          defaultValues={{
            analysisType: analysisData?.inputParameters?.analysisType ?? analysisTypes.RISK_AREAS,
            customPrompt: analysisData?.inputParameters?.customPrompt,
          }}
          onSuccessStartAnalysis={onSuccessStartAnalysis}
          hideBackButton={!analysisData}
        />
      )}
    </Box>
  );
};

const AiStrip = ({ sticky = false }: { sticky?: boolean }) => {
  const theme = useTheme();

  return (
    <Box
      height="2px"
      flexShrink={0}
      sx={{
        backgroundImage: theme.palette.ai.gradient,
        ...(sticky && { position: 'sticky', top: 0, zIndex: 1 }),
      }}
      marginBottom={2}
    />
  );
};

const useSidePanelBreakPoint = () => useMediaQuery(
  (theme) => theme.breakpoints.up(breakpoints.values.xxl),
);

export const AiAnalysisAside = ({
  open,
  closeDialog,
}: {
  open: boolean;
  closeDialog: () => void;
}) => {
  const shouldShowAside = useSidePanelBreakPoint();
  const { permissions } = usePermissions<Actions[]>();

  if (!hasAccess(permissions, actions.ANALYSIS_GET) || !shouldShowAside) return null;

  if (!open) return null;

  return (
    <Card
      variant="outlined"
      sx={{
        marginTop: 5,
        marginLeft: 2,
        width: 450,
        minWidth: 450,
        maxWidth: 450,
        flex: '0 0 450px',
        top: 144,
        alignSelf: 'stretch',
      }}
    >
      <AiStrip />
      <AiPanelContent closeDialog={closeDialog} />
    </Card>
  );
};

export const AiAnalysisDrawer = ({
  open,
  closeDialog,
}: {
  open: boolean;
  closeDialog: () => void;
}) => {
  const shouldShowAside = useSidePanelBreakPoint();
  const { permissions } = usePermissions<Actions[]>();

  if (!hasAccess(permissions, actions.ANALYSIS_GET) || shouldShowAside) return null;

  return (
    <Drawer
      open={open}
      onClose={closeDialog}
      anchor="right"
      slotProps={{
        paper: {
          sx: {
            maxWidth: 450,
            width: '100%',
          },
        },
      }}
    >
      {/* The Drawer paper scrolls, so the strip sticks to stay at the top. */}
      <AiStrip sticky />
      <AiPanelContent closeDialog={closeDialog} />
    </Drawer>
  );
};
